export function validateInputs(inputs) {
  const issues = [];

  const deposit = inputs.deposit ?? 0;
  const amountFinanced = inputs.assetCost - deposit;

  // Business rule: deposit cannot exceed or equal asset cost
  if (deposit >= inputs.assetCost) {
    issues.push("Deposit must be less than asset cost");
  }

  // Business rule: instalments must cover financed amount
  if (inputs.instalmentAmount * inputs.tenor < amountFinanced) {
    const minInstalment = amountFinanced / (inputs.tenor || 1);
    issues.push(
      `The instalment amount and tenor do not cover the financed amount. You need at least an instalment amount of $${minInstalment.toFixed(
        2
      )} to fully cover the amount financed (asset cost minus deposit) of $${amountFinanced.toFixed(
        2
      )}.`
    );
  }

  // Business rule: valid first instalment date
  if (!(inputs.firstInstalmentDate instanceof Date)) {
    issues.push("Invalid first instalment date");
  }

  return {
    valid: issues.length === 0,
    issues,
  };
}

/**
 * Validate HP schedule reconciliation
 *
 * @param {Array} schedule - HP repayment schedule
 * @param {number} pv - Present value (amount financed)
 * @param {number} pmt - Payment amount (absolute value)
 * @param {number} n - Number of instalments
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function validateSchedule(schedule, pv, pmt, n) {
  const errors = [];
  const TOLERANCE = 0.01; // ±0.01 as per PRD

  // Calculate sums
  // Exclude Instalment 0 (deposit) from principal sum - only sum principal from instalments 1 to N
  // This is because PV = Asset Cost - Deposit, so we should only compare against financed amount
  const sumPrincipal = schedule
    .filter((row) => row.instalmentNo !== 0) // Exclude deposit (Instalment 0)
    .reduce((sum, row) => sum + row.principal, 0);
  const sumInterest = schedule.reduce((sum, row) => sum + row.interest, 0);
  const expectedInterest = Math.abs(pmt) * n - pv;

  // Validation: Sum of Principal = PV (±0.01)
  // Note: This excludes the deposit (Instalment 0) from the principal sum
  if (Math.abs(sumPrincipal - pv) > TOLERANCE) {
    errors.push(
      `Reconciliation failure: Sum of Principal (${sumPrincipal.toFixed(
        2
      )}) does not equal PV (${pv.toFixed(2)}). Difference: ${Math.abs(
        sumPrincipal - pv
      ).toFixed(2)}`
    );
  }

  // Validation: Sum of Interest = (|PMT| × N) − PV (±0.01)
  if (Math.abs(sumInterest - expectedInterest) > TOLERANCE) {
    errors.push(
      `Reconciliation failure: Sum of Interest (${sumInterest.toFixed(
        2
      )}) does not equal expected (${expectedInterest.toFixed(
        2
      )}). Difference: ${Math.abs(sumInterest - expectedInterest).toFixed(2)}`
    );
  }

  return {
    valid: errors.length === 0,
    errors,
    reconciliation: {
      sumPrincipal,
      sumInterest,
      expectedPrincipal: pv,
      expectedInterest,
      principalDifference: Math.abs(sumPrincipal - pv),
      interestDifference: Math.abs(sumInterest - expectedInterest),
    },
  };
}

export function validateStep2Inputs(inputs) {
  const missingFields = [];

  if (
    inputs.fyEndMonth === undefined ||
    inputs.fyEndMonth === null ||
    inputs.fyEndMonth === ""
  ) {
    missingFields.push("Company financial year-end month");
  } else if (
    typeof inputs.fyEndMonth !== "number" ||
    inputs.fyEndMonth < 1 ||
    inputs.fyEndMonth > 12
  ) {
    missingFields.push("Company financial year-end month (must be 1-12)");
  }

  if (!inputs.caType || inputs.caType === "") {
    missingFields.push("CA Type");
  }

  if (!inputs.assetName || inputs.assetName.trim() === "") {
    missingFields.push("Asset Name");
  }

  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}
